// keys regex expression
// eslint-disable-next-line max-classes-per-file
const EXPR_REPLACEMENT_EXPRESSION = /#([a-zA-Z0-9]+)?/gm;
const REPL_REPLACEMENT_EXPRESSION = /(#[\w+.?]+)/gm;
const REPL_FULL_REPLACEMENT_EXPRESSION = /(#[\w+.?]+)(::\[[^[\]]*\])?/gm;

const SUFFIXES = {
  EXPRESSION: 'expr',
  REPLACEMENT: 'repl',
};

const getSuffixKey = (key) => (Object.entries(SUFFIXES)
  .find((entry) => key.endsWith(entry[1])) || [])[0];

function parseOptionalChainingExpression(expression) {
  let parsed = expression;
  const openingToken = '?.';
  expression.split(' ')
    // eslint-disable-next-line no-useless-escape
    .filter((token) => /(this\.[\w+\?.?]+)/.test(token) && token.includes(openingToken))
    .forEach((expr) => {
      const replacement = expr.split(openingToken)
        .reduce((tot, curr) => `(${tot} || {}).${curr}`);

      parsed = parsed.replace(expr, replacement);
    });

  return parsed;
}

const EXPRESSIONS = {
  repl: (expression) => {
    let evalExpr = expression
      .replace(/'/g, '\\\'');

    // check if full replacement expressions exists
    const matches = [...evalExpr.matchAll(REPL_FULL_REPLACEMENT_EXPRESSION)]
      .filter((match) => typeof match[2] === 'string');
    if (matches.length) {
      matches.forEach(([full, $1, $2]) => {
        const firstPart = $1.replace(EXPR_REPLACEMENT_EXPRESSION, 'this.$1')
          .split('.')
          .reduce((tot, curr) => `(${tot} || {}).${curr}`);
        const secondPart = $2.slice(3, -1);
        evalExpr = evalExpr
          .replace(full, `' + (${firstPart}?${firstPart}.replace(/^(.*)$/gm, '${secondPart}'):'') + '`);
      });
    }

    evalExpr = evalExpr
      .replace(REPL_REPLACEMENT_EXPRESSION, (matched) => {
        const reducedExpr = matched.split('.').reduce((acum, attr) => `(${acum} || {}).${attr}`);
        return `' + (${reducedExpr} || '') + '`;
      })
      .replace(EXPR_REPLACEMENT_EXPRESSION, 'this.$1');

    console.log('[REPL] parsed from ', expression, 'to', evalExpr);

    return `"use strict";return '${evalExpr}'`;
  },
  expr: (expression) => {
    let evalExpr = expression
      .replace(EXPR_REPLACEMENT_EXPRESSION, 'this.$1');

    // optional chaining expressions not supported by IE
    if (typeof window?.navigator?.userAgent === 'string'
      && (window.navigator.userAgent.includes('MSIE') || window.navigator.userAgent.includes('rv:'))) {
      evalExpr = parseOptionalChainingExpression(evalExpr);
    }

    console.log('[EXPR] parsed from ', expression, 'to', evalExpr);
    return `"use strict";return ${evalExpr};`;
  },
};

/**
   * Evaluates an expression from configuration sentence
   * Reserved keys are: element, value, item, user
   *
   * @param {String} expression the expression to be evaluated
   * @param {String} evalType the evaluation type (one of ['repl', 'expr'])
   */
const evalExpression = function evalExpression(expression, evalType) {
  // first step: encapsule the evaluated expression into a function
  const fn = EXPRESSIONS[evalType] ? EXPRESSIONS[evalType] : EXPRESSIONS.expr;
  // eslint-disable-next-line no-new-func
  const expr = Function(fn(expression));
  // final step: return a function which evaluates the expression
  // programatically based on a defined context
  return (context) => {
    try {
      return expr.call(context);
    } catch (ex) {
      console.error('error evaluating expression', ex);
      return expression;
    }
  };
};

const getMatchType = (key, exprKeys, replKeys) => {
  const filterFn = (k) => key === k;
  if (exprKeys.some(filterFn)) {
    return SUFFIXES.EXPRESSION;
  } if (replKeys.some(filterFn)) {
    return SUFFIXES.REPLACEMENT;
  }
  return null;
};

/**
 * @summary evaluates an expression to bind a function.
 * For Key 'sample:expr' and value '#user.name'
 * will return an object { sample: fn(ctx) }
 *
 * @param {*} object the current object to be evaluated
 * @param {*} expressionKeys predefined keys as expressions
 * @param {*} replacementKeys predefined keys as replacements
 * @param {*} matched recursive token that indicates if previous
 *                    key was matched as replacement or expression
 */
const prepare = (object = {}, expressionKeys = [], replacementKeys = [], matched = null) => {
  const match = (key) => (matched || (getMatchType(key, expressionKeys, replacementKeys)));
  Object.keys(object)
    .filter((key) => getSuffixKey(key) != null || match(key))
    .forEach((key) => {
      if (typeof object[key] === 'string') {
        let keyname = key;
        const suffixKey = getSuffixKey(key);
        let fn;
        if (suffixKey != null) {
          const suffix = SUFFIXES[suffixKey];
          keyname = key.substr(0, key.indexOf(suffix) - 1);
          fn = evalExpression(object[key], suffix);

          // eslint-disable-next-line no-param-reassign
          delete object[key];
        } else {
          fn = evalExpression(object[key], match(key));
        }
        // eslint-disable-next-line no-param-reassign
        object[keyname] = (ctx) => fn(ctx);
      } else if (typeof object[key] === 'object') {
        // eslint-disable-next-line no-param-reassign
        object[key] = { ...object[key] };
        prepare(
          object[key],
          expressionKeys,
          replacementKeys,
          match(key),
        );
      } else {
        throw new Error('invalid expression found on key', key, ':', object[key]);
      }
    });
};

class FieldExpressionInterpreter {
  constructor(alias, formInterpreter, boundedArgs) {
    this.alias = alias;
    this.formInterpreter = formInterpreter;
    this.formInterpreter[this.alias] = boundedArgs || {};
  }

  load(key, object = {}, expressionKeys, replacementKeys) {
    this.formInterpreter[this.alias][key] = { ...object };
    prepare(this.formInterpreter[this.alias][key], expressionKeys, replacementKeys);
  }

  eval(key, defaultValue, ctx) {
    const f = key.split('.').reduce((tot, curr, index, arr) => ((typeof tot[curr] === 'undefined' && index < arr.length - 1) ? {} : tot[curr]), this.formInterpreter[this.alias]);
    /* console.log('built context: ', {
      ...this.formInterpreter.staticContextFields,
      ...this.formInterpreter.fieldsContext,
      ...ctx,
    }); */
    return typeof f === 'function' ? f({
      ...this.ctx,
      ...ctx,
    }) : (f || defaultValue);
  }

  get(key) {
    return this.formInterpreter[this.alias][key];
  }

  get ctx() {
    return ({
      ...this.formInterpreter.staticContextFields,
      ...this.formInterpreter.fieldsContext,
    });
  }

  get fields() {
    return this.formInterpreter.fieldsContext;
  }
}

export default class FormExpressionInterpreter {
  constructor(opts = {}) {
    this.staticContextFields = opts.staticContextFields || {};
    this.getFieldsCtx = opts.getFieldsCtx || (() => ({}));
  }

  setContextField(key, value) {
    this.staticContextFields[key] = value;
  }

  interpreter(alias) {
    return new FieldExpressionInterpreter(alias, this, this[alias]);
  }

  get fieldsContext() {
    return this.getFieldsCtx();
  }

  set fieldsContext(data) {
    this.getFieldsCtx = () => data || ({});
  }
}
/**
var EXPRESSION_SUFFIX = '.expr';

var cfg = {
  readonly: "#user.hasRole('tecnicocsu')",
  validation: {
    containsSpace: "!#value.includes(' ')",
  },
  error: {
    containsSpace: 'El campo no puede contener espacios en blanco',
  },
  'max.expr': "#user.hasRole('tecnicocsu') && #value >= 1000",
}
 */
